import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";
import { SectionHeadline } from "./Typography";

const Section = styled.section`
    padding: 5rem 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
`;

const Heading = styled(SectionHeadline)`
    text-align: center;
    margin-bottom: 1rem;
`;

const Subtext = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    max-width: 660px;
    margin: 0 auto 3rem;
`;

const RolesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    margin: 0 auto;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

// Each capability is a terminal readout panel: a bracketed key, a value, a body.
const RoleCard = styled.div`
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-top: 2px solid ${({ color }) => color};
    box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.5);
    padding: 2rem 1.75rem;
    text-align: left;
    position: relative;
    transition: border-color 0.14s ease-out, box-shadow 0.14s ease-out;

    &:hover {
        border-color: ${({ color }) => color};
        box-shadow: 0 0 20px ${({ theme }) => theme.glowSoft},
                    inset 0 0 40px rgba(0, 0, 0, 0.5);
    }
`;

const RoleKey = styled.span`
    display: block;
    font-family: ${typography.fontFamily.display};
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ color }) => color};
    margin-bottom: 0.75rem;
`;

const RoleTitle = styled.h3`
    font-family: ${typography.fontFamily.display};
    font-size: 1.05rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.text};
    margin: 0 0 0.85rem;
`;

const RoleDescription = styled.p`
    font-size: ${typography.fontSize[200]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

const roles = [
    {
        key: "SPEC.01",
        title: "Application Engineering",
        description:
            "Production React and React Native against Node and Next.js services. Authentication, subscriptions, data models, third-party integrations, deployment — the parts that have to keep working after launch.",
        color: colors.primary[500],
    },
    {
        key: "SPEC.02",
        title: "Systems & Architecture",
        description:
            "Component APIs, token pipelines, and the shared infrastructure product teams build on. Drawing boundaries so a change has a known blast radius instead of an unknown one.",
        color: colors.emerald[500],
    },
    {
        key: "SPEC.03",
        title: "Design Fluency",
        description:
            "I can do the design work, which means fewer handoffs, tighter feedback loops, and interfaces that survive contact with real constraints. It is leverage on the engineering, not a separate job.",
        color: colors.amber[400],
    },
];

const RolesSection = () => (
    <Section>
        <Heading>Operator Profile</Heading>
        <Subtext>
            An engineer who designs. I write the software, own the architecture underneath it, and
            can take an interface the whole way without a handoff.
        </Subtext>
        <RolesGrid>
            {roles.map((role) => (
                <RoleCard key={role.key} color={role.color}>
                    <RoleKey color={role.color}>{role.key}</RoleKey>
                    <RoleTitle>{role.title}</RoleTitle>
                    <RoleDescription>{role.description}</RoleDescription>
                </RoleCard>
            ))}
        </RolesGrid>
    </Section>
);

export default RolesSection;
